/**
 * Compound Inequalities generator (AND / OR).
 *
 * AND: a compound bound like  lo < a x + b < hi  solving to a bounded interval.
 * OR : two conditions like  a x + b < lo  OR  a x + b > hi  solving to a union.
 * Bounds are chosen first, then the linear inner expression is applied and the
 * resulting interval/union is computed by solving each piece with the
 * independent LinearInequality solver and combining.
 *
 * Families:
 *   C1: AND, simple    d < x + b < e
 *   C2: AND, coefficient  d < ax + b < e
 *   C3: OR,  simple    x + b < d  or  x + b > e
 *   C4: OR,  coefficient
 */

import { Rational, rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearExpr } from "../engine/linear";
import {
  LinearInequality,
  inSolutionSet,
  solutionSetToAnswer,
  solutionSetLatex,
  type SolutionSet,
} from "../engine/inequality";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "compound-inequalities" as const;
const VERSION = "1.0.0";
const VARS = ["x", "n", "a", "m", "p"] as const;

type Family = "C1" | "C2" | "C3" | "C4";

interface Cfg {
  readonly families: readonly Family[];
  readonly coeffMax: number;
  readonly bMax: number;
  readonly boundMax: number;
  readonly pNeg: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["C1", "C3"], coeffMax: 1, bMax: 6, boundMax: 10, pNeg: 0.15 },
  standard: { families: ["C1", "C2", "C3"], coeffMax: 4, bMax: 8, boundMax: 12, pNeg: 0.35 },
  hard: { families: ["C2", "C3", "C4"], coeffMax: 6, bMax: 10, boundMax: 14, pNeg: 0.45 },
  challenge: { families: ["C2", "C4"], coeffMax: 8, bMax: 12, boundMax: 16, pNeg: 0.5 },
};

function coeffL(c: Rational, v: string): string {
  if (c.equals(Rational.ONE)) return v;
  if (c.equals(Rational.of(-1n))) return `-${v}`;
  return `${c.toLatex()}${v}`;
}
function plusL(r: Rational): string {
  return r.isNegative() ? `- ${r.neg().toLatex()}` : `+ ${r.toLatex()}`;
}
const le = "\\le";

interface Built {
  family: Family;
  variable: string;
  displayLatex: string;
  set: SolutionSet;
  connector: "and" | "or";
}

/** Solve  inner (op) bound  as a ray, given inner = a x + b. */
function ray(a: Rational, b: Rational, op: "<" | "<=" | ">" | ">=", bound: Rational) {
  const ineq = new LinearInequality(new LinearExpr(a, b), op, LinearExpr.constantExpr(bound));
  const sol = ineq.solve();
  return sol; // ray | all | none
}

function build(rng: Rng, difficulty: Difficulty): Built | null {
  const cfg = DIFF[difficulty];
  const v = rng.pick(VARS);
  const family = rng.pick(cfg.families);
  const withCoeff = family === "C2" || family === "C4";
  const isOr = family === "C3" || family === "C4";

  const a = withCoeff ? rat(rng.int(2, cfg.coeffMax < 2 ? 2 : cfg.coeffMax) * rng.sign(cfg.pNeg)) : Rational.ONE;
  if (a.isZero()) return null;
  const b = rat(rng.int(1, cfg.bMax) * rng.sign(cfg.pNeg));

  // Two distinct bounds lo < hi for the inner expression's comparison.
  const lo = rng.int(-cfg.boundMax, cfg.boundMax - 2);
  const hi = rng.int(lo + 2, cfg.boundMax);
  const loR = rat(lo);
  const hiR = rat(hi);
  const loIncl = rng.bool();
  const hiIncl = rng.bool();

  if (!isOr) {
    // AND:  lo (<|<=) inner (<|<=) hi
    const lowRay = ray(a, b, loIncl ? ">=" : ">", loR); // inner >= lo
    const highRay = ray(a, b, hiIncl ? "<=" : "<", hiR); // inner <= hi
    if (lowRay.kind !== "ray" || highRay.kind !== "ray") return null;
    // Combine into an interval on x. Because a may be negative, the ray ops may
    // have flipped; determine the actual [xLo,xHi] by ordering the two boundary
    // x-values and pairing inclusivity.
    const set = intersectRays(lowRay, highRay);
    if (!set || set.kind !== "interval") return null;
    if (set.lo.gte(set.hi)) return null; // empty/degenerate
    const loOp = loIncl ? le : "<";
    const hiOp = hiIncl ? le : "<";
    const inner = `${coeffL(a, v)} ${plusL(b)}`;
    const displayLatex = `${loR.toLatex()} ${loOp} ${inner} ${hiOp} ${hiR.toLatex()}`;
    return { family, variable: v, displayLatex, set, connector: "and" };
  }

  // OR:  inner (<|<=) lo  OR  inner (>|>=) hi
  const lowRay = ray(a, b, loIncl ? "<=" : "<", loR);
  const highRay = ray(a, b, hiIncl ? ">=" : ">", hiR);
  if (lowRay.kind !== "ray" || highRay.kind !== "ray") return null;
  const set = unionRays(lowRay, highRay);
  if (!set || set.kind !== "union") return null;
  if (set.lo.gte(set.hi)) return null;
  const loOp = loIncl ? le : "<";
  const hiOp = hiIncl ? "\\ge" : ">";
  const inner = `${coeffL(a, v)} ${plusL(b)}`;
  const displayLatex = `${inner} ${loOp} ${loR.toLatex()} \\;\\text{or}\\; ${inner} ${hiOp} ${hiR.toLatex()}`;
  return { family, variable: v, displayLatex, set, connector: "or" };
}

type Ray = { kind: "ray"; op: "<" | "<=" | ">" | ">="; value: Rational };

/** Intersect two rays into a bounded interval on x (if they bound both sides). */
function intersectRays(r1: Ray, r2: Ray): SolutionSet | null {
  // One ray should be a lower bound (>, >=), the other an upper (<, <=).
  const lower = [r1, r2].find((r) => r.op === ">" || r.op === ">=");
  const upper = [r1, r2].find((r) => r.op === "<" || r.op === "<=");
  if (!lower || !upper) return null;
  return {
    kind: "interval",
    lo: lower.value,
    loInclusive: lower.op === ">=",
    hi: upper.value,
    hiInclusive: upper.op === "<=",
  };
}

/** Union two outward rays into a union set (lower piece + upper piece). */
function unionRays(r1: Ray, r2: Ray): SolutionSet | null {
  const lowPiece = [r1, r2].find((r) => r.op === "<" || r.op === "<=");
  const highPiece = [r1, r2].find((r) => r.op === ">" || r.op === ">=");
  if (!lowPiece || !highPiece) return null;
  if (lowPiece.value.gte(highPiece.value)) return null; // overlapping -> all reals
  return {
    kind: "union",
    lo: lowPiece.value,
    loInclusive: lowPiece.op === "<=",
    hi: highPiece.value,
    hiInclusive: highPiece.op === ">=",
  };
}

/** Independently verify the set by probing representative points. */
function verify(b: Built): boolean {
  if (b.set.kind === "interval") {
    const mid = b.set.lo.add(b.set.hi).div(rat(2));
    return (
      inSolutionSet(b.set, mid) &&
      !inSolutionSet(b.set, b.set.lo.sub(rat(1))) &&
      !inSolutionSet(b.set, b.set.hi.add(rat(1)))
    );
  }
  if (b.set.kind === "union") {
    const mid = b.set.lo.add(b.set.hi).div(rat(2));
    return (
      !inSolutionSet(b.set, mid) &&
      inSolutionSet(b.set, b.set.lo.sub(rat(1))) &&
      inSolutionSet(b.set, b.set.hi.add(rat(1)))
    );
  }
  return false;
}

export class CompoundInequalitiesGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 300; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const b = build(rng, ctx.difficulty);
      if (!b) continue;
      if (!verify(b)) continue;
      const answer = solutionSetToAnswer(b.set);

      return {
        id: `${TOPIC}-${ctx.seed}-${b.family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family: b.family,
        difficulty: ctx.difficulty,
        prompt: { text: b.displayLatex, latex: b.displayLatex },
        answerType: "compound",
        answer,
        steps: [
          {
            description:
              b.connector === "and"
                ? "Solve both bounds; the solution is where both are true (between the bounds)."
                : "Solve each piece; the solution is where either is true (outside the bounds).",
            latex: solutionSetLatex(b.set, b.variable),
          },
        ],
        hints: [
          {
            level: 1,
            text:
              b.connector === "and"
                ? "AND means the value must satisfy both conditions at once."
                : "OR means the value satisfies at least one condition.",
          },
          { level: 2, text: "Apply the same operation to all parts, flipping signs if you multiply/divide by a negative." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 300 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const a = q.answer;
    const structure =
      a.kind === "compound"
        ? `${a.connector}:${a.lower.value.key()}:${a.upper.value.key()}`
        : a.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const compoundInequalities = new CompoundInequalitiesGenerator();
